/* eslint-disable @typescript-eslint/indent */
import dayjs from 'dayjs';
import { ADD_TO_CART } from '@constants/amplitudeEvents';
import inventoryTypes from '@constants/inventoryTypes';
import { CartItemDetails } from '@customTypes/cart-items';
import { UserAction } from '@customTypes/context';
import Product, { ExtendedProductAddon } from '@customTypes/product';
import { GuestGroupType } from '@customTypes/product-details';
import User from '@customTypes/user';
import { useEvents } from '@events/EventsProvider';
import { createOrUpdateCartItems } from '@helpers/cartFunctions';
import { getDateWithDashes } from '@helpers/date';
import { Dispatch } from 'react';
import HotelDetails from '@customTypes/hotel-details';
import { calculateTotalPrice } from '@helpers/product';
import { CartItemPropertyGroupType } from '@customTypes/amplitude';
import { isArray } from 'lodash';
import useAmplitudePayloads from './useAmplitudePayloads';

function useAddToCart() {
  const { track } = useEvents();
  const { generateProductPayload } = useAmplitudePayloads();

  async function addToCart(
    addOnItems: ExtendedProductAddon[],
    adultsCount: number,
    childrenCount: number,
    date: Date,
    cartData: CartItemDetails | undefined,
    product: Product,
    hotel: HotelDetails,
    dispatch: Dispatch<UserAction>,
    user: User,
    guestsGroupBag?: GuestGroupType[],
  ) {
    const items = guestsGroupBag
      ? guestsGroupBag.map((group, index) => ({
          addons:
            index === 0
              ? addOnItems
                  .filter((item) => item.isChecked)
                  .map((item) => ({
                    addon_id: +item.id,
                    units: item.units,
                  }))
              : [],
          booked_date: date ? getDateWithDashes(date) : '',
          hotel_id: hotel.metadata.hotel_id,
          no_of_adults: group.adults,
          no_of_childs: group.children,
          no_of_infants: 0,
          product_id: product.product_id,
        }))
      : [
          {
            addons: addOnItems
              .filter((item) => item.isChecked)
              .map((item) => ({
                addon_id: +item.id,
                units: item.units,
              })),
            booked_date: date ? getDateWithDashes(date) : '',
            hotel_id: hotel.metadata.hotel_id,
            no_of_adults: adultsCount,
            no_of_childs: childrenCount,
            no_of_infants: 0,
            product_id: product.product_id,
          },
        ];

    const cartItems = {
      inventory_type: inventoryTypes.RESORT_PASS,
      items,
    };

    const updatedCartData = await createOrUpdateCartItems(
      cartItems,
      date,
      cartData,
      product.product_id,
      hotel.metadata.hotel_id,
      dispatch,
      user,
    );

    // Amplitude - ADD_TO_CART
    items.forEach((item) => {
      const adultNumber = product.individual_billing ? adultsCount : item.no_of_adults;
      const childrenNumber = product.individual_billing ? childrenCount : item.no_of_childs;

      const cartItemPropertyGroup: CartItemPropertyGroupType = {
        cart_item_adult_price: +product.adult_price,
        cart_item_child_price: +product.children_price,
        cart_item_no_of_adults: adultNumber,
        cart_item_no_of_child: childrenNumber,
        cart_item_no_of_infants: item.no_of_infants,
        cart_item_product_addon_ids_selected: item.addons.map((addon) => addon.addon_id),
        cart_item_product_addon_names_selected: item.addons.map((addon) => {
          const matchingAddon = product.addons.find((a) => addon.addon_id === a.id);
          return matchingAddon ? matchingAddon.name : '';
        }),
        cart_item_product_addon_prices_selected: item.addons.map((addon) => {
          const matchingAddon = product.addons.find((a) => addon.addon_id === a.id);
          return matchingAddon ? matchingAddon.price.toString() : '';
        }),
        cart_item_product_id: item.product_id,
        cart_item_timestamp: dayjs().unix(),
        cart_item_total_adult_price: product.individual_billing
          ? +product.adult_price * adultNumber
          : calculateTotalPrice(product, adultNumber, childrenNumber, undefined),
        cart_item_total_child_price: product.individual_billing
          ? +product.children_price * childrenNumber
          : 0,
        cart_item_total_price: calculateTotalPrice(
          product,
          adultNumber,
          childrenNumber,
          item.addons,
        ),
      };

      const hotelPropertyGroup = {
        hotel_city_id: hotel.city_id,
        hotel_city_name: hotel.city_name,
        hotel_id: hotel.metadata.hotel_id,
        hotel_name: hotel.name,
        hotel_region: isArray(hotel.region) ? hotel.region[0] : hotel.region,
        hotel_star_classification: hotel.star_classification,
        hotel_state_name: hotel.state_name,
        hotel_vibes:
          hotel.vibes && hotel.vibes.primary
            ? [hotel.vibes.primary, hotel.vibes.secondary || ''].filter((vibe) => vibe !== '')
            : [],
      };

      const productPropertyGroup = generateProductPayload(product);

      track(ADD_TO_CART, {
        cart_id: cartData?.id || null,
        ...cartItemPropertyGroup,
        ...hotelPropertyGroup,
        hotel_is_hot_spot: !!hotel.hot_spot,
        has_offer_visible: !!product.product_offer,
        offer_type: product.product_offer,
        products: [productPropertyGroup],
      });
    });

    return { cartItems, updatedCartData };
  }

  return [addToCart];
}

export default useAddToCart;
